import { z } from "zod";

/**
 * Error envelope for every API error: `{ error: { code, message, details } }` (ARCHITECTURE.md 5).
 * Cross-tenant access is always reported as `not_found`, never `forbidden`, so an attacker
 * cannot enumerate the ids of other tenants (CLAUDE.md rule 1).
 */
export const ERROR_STATUS = {
  bad_request: 400,
  validation_failed: 400,
  tenant_required: 400,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  hold_conflict: 409,
  gone: 410,
  hold_expired: 410,
  hold_not_found: 410,
  payload_too_large: 413,
  unsupported_media_type: 415,
  idempotency_mismatch: 422,
  /** The business is suspended or canceled: it can look, but not change anything (BIL-7). */
  account_paused: 423,
  upgrade_required: 426,
  rate_limited: 429,
  internal: 500,
  not_implemented: 501,
  not_configured: 503,
} as const;

export type ErrorCode = keyof typeof ERROR_STATUS;

export function isErrorCode(code: string): code is ErrorCode {
  return Object.prototype.hasOwnProperty.call(ERROR_STATUS, code);
}

export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    requestId: z.string().optional(),
  }),
});

export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: unknown;

  constructor(code: ErrorCode, message?: string, details?: unknown) {
    super(message ?? defaultMessage(code));
    this.name = "ApiError";
    this.code = code;
    this.status = ERROR_STATUS[code];
    this.details = details;
  }

  toEnvelope(requestId?: string): ErrorEnvelope {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.details === undefined ? {} : { details: this.details }),
        ...(requestId ? { requestId } : {}),
      },
    };
  }

  static notFound(what = "That was not found."): ApiError {
    return new ApiError("not_found", what);
  }
}

function defaultMessage(code: ErrorCode): string {
  switch (code) {
    case "unauthorized":
      return "Please sign in.";
    case "forbidden":
      return "You do not have permission to do that.";
    case "not_found":
      return "That was not found.";
    case "validation_failed":
      return "Some of the information is missing or invalid.";
    case "tenant_required":
      return "Choose a business first.";
    case "conflict":
      return "That conflicts with something that already exists.";
    case "idempotency_mismatch":
      return "This request key was already used with different data.";
    case "account_paused":
      return "This business account is paused, so changes are turned off.";
    case "rate_limited":
      return "Too many requests. Please wait a moment.";
    case "hold_conflict":
      return "That time was just taken. Please pick another.";
    case "hold_expired":
    case "hold_not_found":
      return "That time is no longer held. Please pick again.";
    case "gone":
      return "That link has expired.";
    case "upgrade_required":
      return "This endpoint requires a WebSocket connection.";
    case "not_implemented":
      return "Not available yet.";
    case "not_configured":
      return "This is not set up yet.";
    case "payload_too_large":
      return "That file is too large.";
    case "unsupported_media_type":
      return "That kind of file is not supported.";
    case "bad_request":
      return "The request could not be understood.";
    case "internal":
      return "Something went wrong on our side. Please try again.";
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}
